import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, RotateCcw, CheckCircle2, Square, XOctagon, AlertTriangle, ChevronDown, Loader2, ShieldCheck, Zap } from "lucide-react";
import { nexsysApi } from "./api";
import { Pill, Panel } from "./ui";
import { useNexsysGate } from "./gate";

const STATE_TONE = {
  READY: "cyan", RUNNING: "emerald", PAUSED: "indigo", ESCALATED: "amber",
  COMPLETED: "emerald", STOPPED: "indigo", FAILED: "magenta", REVOKED: "magenta",
};
const TERMINAL = ["COMPLETED", "STOPPED", "FAILED", "REVOKED"];
const ACTIONS_FOR = {
  READY: ["start", "stop", "revoke"],
  RUNNING: ["pause", "complete", "stop", "revoke"],
  PAUSED: ["resume", "stop", "fail", "revoke"],
  ESCALATED: ["resume", "stop", "revoke"],
};
const ACTION_META = {
  start: { label: "Start", Icon: Play, tone: "emerald" },
  pause: { label: "Pause", Icon: Pause, tone: "indigo" },
  resume: { label: "Resume", Icon: RotateCcw, tone: "cyan" },
  complete: { label: "Complete", Icon: CheckCircle2, tone: "emerald" },
  stop: { label: "Stop", Icon: Square, tone: "indigo" },
  fail: { label: "Fail", Icon: XOctagon, tone: "magenta" },
  revoke: { label: "Revoke", Icon: XOctagon, tone: "magenta" },
};
const BTN_TONE = {
  emerald: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  cyan: "border-cyan-400/30 bg-cyan-400/10 text-cyan-300",
  indigo: "border-indigo-400/30 bg-indigo-400/10 text-indigo-300",
  magenta: "border-pink-400/30 bg-pink-400/10 text-pink-300",
};

// Governed refusals render VERBATIM — fail closed, never masked, softened, or retried.
function RefusalBlock({ id, payload }) {
  const det = payload && typeof payload.detail !== "undefined" ? payload.detail : payload;
  const reasons = (payload && payload.reasons) || (det && det.reasons) || [];
  const outcomes = (payload && payload.outcomes) || (det && det.outcomes) || [];
  const code = det && typeof det === "object" ? det.code : null;
  const reason = det && typeof det === "object" ? det.reason : (typeof det === "string" ? det : null);
  return (
    <div className="mt-2 rounded-lg border border-pink-400/25 bg-pink-400/[0.06] px-3 py-2" data-testid={`refusal-${id}`}>
      <div className="font-mono text-[10px] font-semibold tracking-wider text-pink-300">GOVERNED REFUSAL — DISPLAYED VERBATIM (FAIL CLOSED)</div>
      {code && <div className="mt-1 font-mono text-[11px] text-pink-200" data-testid={`refusal-code-${id}`}>{code}</div>}
      {reason && <div className="mt-0.5 text-[12px] text-pink-200/90">{reason}</div>}
      {reasons.map((r, i) => (
        <div key={i} className="mt-0.5 text-[12px] text-pink-200/90" data-testid={`refusal-reason-${id}-${i}`}>{r}</div>
      ))}
      {outcomes.map((o, i) => (
        <div key={i} className="mt-0.5 flex items-start gap-2 text-[12px]">
          <span className="font-mono text-[10px] text-amber-300">{o.code}</span>
          <span className="text-white/60">{o.detail}</span>
        </div>
      ))}
      <details className="mt-1.5">
        <summary className="cursor-pointer font-mono text-[10px] text-white/35">raw response</summary>
        <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all font-mono text-[10px] text-white/45">{JSON.stringify(payload, null, 1)}</pre>
      </details>
    </div>
  );
}

function AuthorizableRow({ d, isAdmin, onMaterialized }) {
  const [auth, setAuth] = useState(null);
  const [refusal, setRefusal] = useState(null);
  const [busy, setBusy] = useState("");

  const authorize = async () => {
    setBusy("auth"); setRefusal(null);
    try {
      setAuth(await nexsysApi.authorizeExecution(d.id));
    } catch (e) {
      setRefusal(e.body && Object.keys(e.body).length ? e.body : { detail: e.message });
    }
    setBusy("");
  };

  const execute = async () => {
    setBusy("exec"); setRefusal(null);
    try {
      const r = await nexsysApi.executeDecision(d.id, {
        authorization_id: auth.id, version: auth.decision_version, policy_id: auth.policy_id,
      });
      if (r.executed) { setAuth(null); onMaterialized(); }
      else setRefusal(r); // 200 ineligibility determination — surfaced verbatim
    } catch (e) {
      setRefusal(e.body && Object.keys(e.body).length ? e.body : { detail: e.message });
    }
    setBusy("");
  };

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3" data-testid={`authorizable-row-${d.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-white/90">{d.title || d.id}</div>
          <div className="font-mono text-[10px] text-white/35">
            id {d.id} · v{d.version || 1} · {d.source?.system ? `source ${d.source.system}` : "governed memory"} · status {d.status}
          </div>
        </div>
        {!isAdmin ? (
          <span className="text-[11px] text-white/40" data-testid={`authorizable-readonly-${d.id}`}>
            Read-only — authorize/execute require admin authority (server-enforced)
          </span>
        ) : !auth ? (
          <button onClick={authorize} disabled={!!busy} data-testid={`authorize-btn-${d.id}`}
            className={`flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] transition-opacity disabled:opacity-40 ${BTN_TONE.cyan}`}>
            {busy === "auth" ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}Authorize execution
          </button>
        ) : (
          <button onClick={execute} disabled={!!busy} data-testid={`execute-btn-${d.id}`}
            className={`flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] transition-opacity disabled:opacity-40 ${BTN_TONE.emerald}`}>
            {busy === "exec" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}Execute
          </button>
        )}
      </div>
      {auth && (
        <div className="mt-2 rounded-lg border border-cyan-400/20 bg-cyan-400/[0.05] px-3 py-1.5 font-mono text-[10px] text-cyan-200/80" data-testid={`auth-granted-${d.id}`}>
          AUTHORIZATION GRANTED · {auth.id} · v{auth.decision_version} · policy {auth.policy_id} · expires {String(auth.expires_at || "").slice(0, 16)}Z — authorization never executes; execution re-validates (D,V,A,P,S) now
        </div>
      )}
      {refusal && <RefusalBlock id={d.id} payload={refusal} />}
    </div>
  );
}

function InstanceRow({ g, onAction, busy }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const esc = g.escalation?.outcomes || [];
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail) {
      try { setDetail(await nexsysApi.executionDetail(g.id)); } catch { /* keep row data */ }
    }
  };
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02]" data-testid={`gei-row-${g.id}`}>
      <div className="flex flex-wrap items-center gap-3 p-3">
        <button onClick={toggle} className="flex min-w-0 flex-1 items-center gap-3 text-left" data-testid={`gei-expand-${g.id}`}>
          <ChevronDown className={`h-4 w-4 shrink-0 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-white/90">{g.decision_title || g.decision_id}</div>
            <div className="font-mono text-[10px] text-white/35">
              v{g.decision_version} · by {g.created_by} · updated {String(g.updated_at || "").slice(0, 16)}Z
            </div>
          </div>
        </button>
        {g.state === "ESCALATED" && <AlertTriangle className="h-4 w-4 text-amber-400" />}
        <Pill tone={STATE_TONE[g.state] || "cyan"} data-testid={`gei-state-${g.id}`}>{g.state}</Pill>
        <div className="flex items-center gap-1.5">
          {(ACTIONS_FOR[g.state] || []).map((a) => {
            const m = ACTION_META[a];
            return (
              <button key={a} disabled={busy} onClick={() => onAction(g, a)} data-testid={`gei-${a}-${g.id}`}
                title={a === "resume" && g.state === "ESCALATED" ? "Acknowledge escalation and rebaseline against current governed state" : m.label}
                className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] transition-opacity disabled:opacity-40 ${BTN_TONE[m.tone]}`}>
                <m.Icon className="h-3 w-3" />{m.label}
              </button>
            );
          })}
          {TERMINAL.includes(g.state) && <span className="font-mono text-[10px] text-white/30">TERMINAL</span>}
        </div>
      </div>
      {g.state === "ESCALATED" && esc.length > 0 && (
        <div className="border-t border-amber-400/15 bg-amber-400/[0.04] px-4 py-2.5" data-testid={`gei-escalation-${g.id}`}>
          {esc.map((o, i) => (
            <div key={i} className="flex items-start gap-2 py-0.5 text-[12px]">
              <span className="mt-0.5 font-mono text-[10px] text-amber-300">{o.code}</span>
              <span className="text-white/60">{o.detail}</span>
            </div>
          ))}
        </div>
      )}
      {open && (
        <div className="border-t border-white/[0.06] px-4 py-3" data-testid={`gei-history-${g.id}`}>
          {!detail ? (
            <div className="flex items-center gap-2 text-white/40 text-xs"><Loader2 className="h-3 w-3 animate-spin" /> Loading lifecycle…</div>
          ) : (
            <div className="space-y-1.5">
              {(detail.state_history || []).map((h, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 font-mono text-[10px] text-white/45">
                  <span className="text-cyan-300/80">{h.from || "·"} → {h.to}</span>
                  <span>{String(h.at || "").slice(0, 19)}Z</span>
                  <span className="text-white/30">by {h.by}</span>
                  <span className="min-w-0 flex-1 truncate text-white/50" title={h.reason}>{h.reason}</span>
                  {(h.outcome_codes || []).map((c) => <span key={c} className="text-amber-300/80">{c}</span>)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ExecutionConsole() {
  const { identity } = useNexsysGate();
  const isAdmin = !!identity?.is_admin;
  const [instances, setInstances] = useState(null);
  const [memoryRows, setMemoryRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [d, m] = await Promise.all([nexsysApi.executions(), nexsysApi.memory(50)]);
      setInstances(d.instances || []);
      setMemoryRows(m.memory || []);
    } catch { /* keep last */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const onAction = async (g, action) => {
    setBusy(true); setError("");
    try {
      await nexsysApi.executionTransition(g.id, action);
    } catch (e) {
      const d = e?.body?.detail;
      setError(typeof d === "object" ? `${d.detail}${d.allowed_actions ? ` — allowed: ${d.allowed_actions.join(", ")}` : ""}` : String(d || e.message || "Transition rejected"));
    }
    await load();
    setBusy(false);
  };

  const active = (instances || []).filter((g) => !TERMINAL.includes(g.state));
  const escalated = active.filter((g) => g.state === "ESCALATED").length;
  const done = (instances || []).filter((g) => TERMINAL.includes(g.state));
  const activeDecisionIds = new Set(active.map((g) => g.decision_id));
  const completedDecisionIds = new Set(done.filter((g) => g.state === "COMPLETED").map((g) => g.decision_id));
  const authorizable = (memoryRows || []).filter(
    (d) => d.status === "approved" && !d.retired && !activeDecisionIds.has(d.id) && !completedDecisionIds.has(d.id)
  );

  return (
    <div className="space-y-4" data-testid="execution-console">
      <Panel title="Authorizable Decisions" subtitle="APPROVED GOVERNED DECISION OBJECTS · AUTHORIZATION PRECEDES EXECUTION (EXEC-1..3)"
        testid="authorizable-panel"
        right={<Pill tone="cyan">{authorizable.length} AUTHORIZABLE</Pill>}>
        {memoryRows === null ? (
          <div className="py-6 text-center font-mono text-[11px] text-white/30">LOADING GOVERNED DECISION OBJECTS…</div>
        ) : authorizable.length === 0 ? (
          <div className="py-6 text-center text-sm text-white/40" data-testid="authorizable-empty">
            No approved governed Decision Objects are awaiting execution — decisions registered via the Decision Center appear here until a governed instance is active for them.
          </div>
        ) : (
          <div className="space-y-2">
            {authorizable.map((d) => (
              <AuthorizableRow key={d.id} d={d} isAdmin={isAdmin} onMaterialized={load} />
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Governed Executions" subtitle="LIFECYCLE-GOVERNED · REVALIDATED AGAINST LIVE ENTERPRISE STATE"
        testid="gei-active-panel"
        right={<div className="flex items-center gap-2">
          {escalated > 0 && <Pill tone="amber">{escalated} ESCALATED</Pill>}
          <Pill tone="cyan">{active.length} ACTIVE</Pill>
        </div>}>
        {error && <div className="mb-3 rounded-xl border border-pink-400/20 bg-pink-400/[0.06] px-3 py-2 text-[12px] text-pink-300" data-testid="gei-error">{error}</div>}
        {instances === null ? (
          <div className="py-8 text-center font-mono text-[11px] text-white/30">LOADING GOVERNED INSTANCES…</div>
        ) : active.length === 0 ? (
          <div className="py-8 text-center text-sm text-white/40" data-testid="gei-empty">
            No active execution instances. Executing an approved decision materializes a governed instance here.
          </div>
        ) : (
          <div className="space-y-2">
            {active.map((g) => <InstanceRow key={g.id} g={g} onAction={onAction} busy={busy} />)}
          </div>
        )}
      </Panel>
      {done.length > 0 && (
        <Panel title="Concluded Instances" subtitle="TERMINAL · IMMUTABLE RECORD" testid="gei-terminal-panel">
          <div className="space-y-2">
            {done.slice(0, 12).map((g) => <InstanceRow key={g.id} g={g} onAction={onAction} busy={busy} />)}
          </div>
        </Panel>
      )}
    </div>
  );
}
